// Rynoka — Custom Scripts
// Includes: Cart AJAX helpers, Sticky ATC, Tab switcher, Pincode checker
// PDP "Frequently bought together" lives in toyland-redesign.js (with formatMoney + cart UI).

// =============================================================================
// CART AJAX HELPERS
// =============================================================================

/* Shopper-facing message for a cart failure.
   Rule for this whole file and toyland-redesign.js: an Add to Cart that does not
   add MUST say why. Silently restoring the button label reads to the shopper as a
   dead button — they click again, nothing happens again, and they leave. Every
   catch/early-return on an add path therefore ends in a toast.
   The real toast lives in toyland-redesign.js, which loads AFTER this file, so it
   is resolved at call time (click time), never at parse time; the inline fallback
   is only for the window before that file has executed. */
function tlToast(message, variant) {
  if (!message) return;
  if (typeof window.tlShowToast === 'function') {
    window.tlShowToast(message, variant || 'error');
    return;
  }
  let host = document.querySelector('[data-tl-toast-host]');
  if (!host) {
    host = document.createElement('div');
    host.setAttribute('data-tl-toast-host', '');
    host.className = 'tl-toast-host';
    document.body.appendChild(host);
  }
  const toast = document.createElement('div');
  toast.className = 'tl-toast tl-toast--' + (variant || 'error');
  toast.setAttribute('role', 'status');
  toast.innerHTML = '<span class="tl-toast__msg"></span>';
  toast.querySelector('.tl-toast__msg').textContent = message;
  host.appendChild(toast);
  requestAnimationFrame(() => toast.classList.add('is-visible'));
  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => toast.remove(), 300);
  }, 3200);
}
window.tlToast = tlToast;

/** Turn any add/update failure into something a shopper can act on. */
function tlCartErrorMessage(err) {
  const raw = err && err.message ? String(err.message).trim() : '';
  if (!raw) return 'Something went wrong — please try again.';
  if (/failed to fetch|networkerror|load failed|network request failed|aborted/i.test(raw)) {
    return 'Network problem — check your connection and try again.';
  }
  return raw;
}
window.tlCartErrorMessage = tlCartErrorMessage;

// Generic AJAX cart add
async function tlAddToCart(variantId, quantity = 1) {
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity })
  });
  let data;
  try {
    data = await res.json();
  } catch (e) {
    throw new Error('Invalid cart response');
  }
  if (!res.ok || (data && data.status)) {
    const err = new Error((data && (data.description || data.message)) || 'Unable to add to cart');
    // Set by the per-order cap in toyland-redesign.js, which has already toasted the
    // reason — callers use this to avoid showing the same message twice.
    if (data && data.tl_limit) err.tlLimit = true;
    throw err;
  }
  const cart = await tlGetCart();
  document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: cart }));
  document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart, bubbles: true }));
  return cart;
}

// Get current cart state
// `cache: 'no-store'` because this read is what every cart surface repaints from:
// a revalidated-but-stale /cart.js would repaint the drawer without the line that
// was just added, which looks exactly like the add having failed.
async function tlGetCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!res.ok) throw new Error('Could not load cart');
  return res.json();
}

// Update cart item quantities
async function tlUpdateCart(updates) {
  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates })
  });
  const data = await res.json();
  if (!res.ok || (data && data.status)) {
    throw new Error((data && (data.description || data.message)) || 'Cart update failed');
  }
  return data;
}

// Update all cart count badges on page
function tlUpdateCartBadge(count) {
  document.querySelectorAll('[data-tl-cart-count]').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

// Listen for cart updates and refresh badge count (skip extra /cart.js when payload already has count)
document.addEventListener('tl:cart:updated', async (e) => {
  const d = e.detail;
  if (d && typeof d.item_count === 'number') {
    tlUpdateCartBadge(d.item_count);
    return;
  }
  const cart = await tlGetCart();
  tlUpdateCartBadge(cart.item_count);
});


// =============================================================================
// QUICK ADD (data-tl-quick-add) — cart drawer upsell + cart recommendation cards
//
// THE SINGLE handler for [data-tl-quick-add]. A second, separate document-level
// click listener for the same selector used to live further down this file. Which
// of the two actually ran came down to registration order: the bottom one was
// registered at parse time and so went first, and it set `btn.disabled = true`
// synchronously — which made THIS one bail on its own `if (btn.disabled) return`.
// So the version that shipped was the bottom one, and this one's Dawn
// `renderContents()` path never executed at all.
//
// That is a bad place to leave a checkout path. Nothing declared the intent, the
// live behaviour hung on one incidental line of one of the two copies, and moving
// either registration — or dropping the `disabled` line — would have turned every
// quick add into two units silently. Neither copy guarded against a second click
// either, so a double-click genuinely did add two.
//
// One handler now, with an explicit re-entry guard. The drawer is repainted from
// one place only: /cart.js via tlAddToCart → 'cart:refresh' → tlRenderCart. Dawn's
// renderContents() is deliberately not reinstated — mixing a server-rendered
// section snapshot with the live /cart.js repaint makes which one lands last a
// matter of timing, and the loser can be a cart the shopper no longer has.
// =============================================================================

function initTlQuickAdd() {
  if (window.__tlQuickAddBound) return;
  window.__tlQuickAddBound = true;

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-tl-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    // Re-entry guard: a double-click (or a click while the first add is still in
    // flight) must not queue a second unit behind the shopper's back.
    if (btn.dataset.tlAdding === '1') return;

    const variantId = btn.dataset.variantId;
    if (!variantId) {
      tlToast('This item can’t be added from here — open the product page.', 'error');
      return;
    }

    btn.dataset.tlAdding = '1';
    btn.disabled = true;
    btn.classList.add('loading');
    // Captured once and stashed, so a restore can never write back a transient
    // "Adding…" label as if it were the original.
    if (btn.dataset.tlOrigHtml == null) btn.dataset.tlOrigHtml = btn.innerHTML;
    const originalHTML = btn.dataset.tlOrigHtml;
    btn.innerHTML = '<span class="tl-btn-loading"></span>';

    const restore = (delay) => {
      setTimeout(() => {
        btn.dataset.tlAdding = '0';
        btn.disabled = false;
        btn.classList.remove('loading');
        btn.innerHTML = originalHTML;
        btn.style.background = '';
      }, delay);
    };

    try {
      const cart = await tlAddToCart(Number(variantId), 1);

      btn.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> Added!';
      btn.style.background = 'var(--tl-green)';

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tl-quick-add', cartData: cart });
      }

      // The "You might also like" grid renders on an EMPTY cart page too, so this is
      // the one add that can flip that page from empty to filled. Dawn's
      // onCartUpdate() only swaps <cart-items>'s innerHTML — the .is-empty class stays
      // on the host, .cart__contents stays display:none, and #main-cart-footer (the
      // order summary) is not in getSectionsToRender() at all. The result is a page
      // still claiming the cart is empty over a cart that isn't. Reload instead: the
      // whole layout changes at that transition anyway, and it only fires once.
      const emptyCartPage = document.querySelector('cart-items.is-empty');
      if (emptyCartPage && cart && cart.item_count > 0) {
        window.location.reload();
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.setActiveElement?.(btn);
        cartDrawer.open(btn);
      }

      restore(1800);
    } catch (err) {
      // tlLimit means the per-order cap already toasted the reason — a second toast
      // saying the same thing just looks broken.
      if (!err || !err.tlLimit) tlToast(tlCartErrorMessage(err), 'error');
      btn.innerHTML = 'Try again';
      btn.style.background = 'var(--tl-red)';
      restore(2000);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTlQuickAdd);
} else {
  initTlQuickAdd();
}


// =============================================================================
// PINCODE CHECKER (snippet: toyland-pincode, /pages/pincode-checker, PDP)
// Modes (theme settings): shopify_shipping = Cart API rates (Shiprocket via Shopify);
// zones = prefix-based ETA estimates when rates are unavailable.
// =============================================================================

function tlPincodeZone(pin) {
  const p = parseInt(pin, 10);
  const p3 = Math.floor(p / 1000);
  const ne = [787,788,789,790,791,792,793,794,795,796,797,798,799,744];
  if (ne.indexOf(p3) > -1) return { zoneKey: 'ne', cod: true };
  const metro = [110,111,400,401,402,403,404,405,406,407,408,410,411,412,413,414,560,561,562,563,564,565,566,567,568,600,601,602,603,604,605,606,607,608,609,610,611,612,613,614,615,616,617,618,619,700,701,702,703,704,705,706,707,708,500,501,502,503,504,505,506,380,381,382,383,384,385,390,391,392,393,394,395];
  if (metro.indexOf(p3) > -1) return { zoneKey: 'metro', cod: true };
  const tier2 = [302,303,304,305,306,226,227,228,229,800,801,802,803,804,462,463,464,465,452,453,454,455,440,441,442,443,444,445,641,642,643,644,645,682,683,684,685,686,160,161,162,163,248,249,250,670,671,672,673,571,572,573,520,521,522,523,530,531,532,480,481,482,483,484,485,360,361,362,363];
  if (tier2.indexOf(p3) > -1) return { zoneKey: 'tier2', cod: true };
  return { zoneKey: 'tier3', cod: true };
}

/** Shopify province code for Indian pincodes (shipping rate API). */
const TL_PINCODE_PROVINCE_PREFIX3 = {
  '110': 'DL', '111': 'DL', '112': 'DL', '113': 'DL', '114': 'DL', '115': 'DL', '116': 'DL', '117': 'DL', '118': 'DL', '119': 'DL', '120': 'HR', '121': 'HR', '122': 'HR', '123': 'HR', '124': 'HR', '125': 'HR', '126': 'HR', '127': 'HR', '128': 'HR', '129': 'HR', '130': 'HR', '131': 'HR', '132': 'HR', '133': 'HR', '134': 'HR', '135': 'HR', '136': 'HR', '140': 'PB', '141': 'PB', '142': 'PB', '143': 'PB', '144': 'PB', '145': 'PB', '146': 'PB', '147': 'PB', '148': 'PB', '149': 'PB', '150': 'PB', '151': 'PB', '152': 'PB', '160': 'CH', '161': 'CH', '162': 'CH', '163': 'CH', '170': 'HP', '171': 'HP', '172': 'HP', '173': 'HP', '174': 'HP', '175': 'HP', '176': 'HP', '177': 'HP', '180': 'JK', '181': 'JK', '182': 'JK', '183': 'JK', '184': 'JK', '185': 'JK', '190': 'JK', '191': 'JK', '192': 'JK', '193': 'JK', '194': 'JK', '200': 'UP', '201': 'UP', '202': 'UP', '203': 'UP', '204': 'UP', '205': 'UP', '206': 'UP', '207': 'UP', '208': 'UP', '209': 'UP', '210': 'UP', '211': 'UP', '212': 'UP', '213': 'UP', '214': 'UP', '215': 'UP', '216': 'UP', '217': 'UP', '218': 'UP', '219': 'UP', '220': 'UP', '221': 'UP', '222': 'UP', '223': 'UP', '224': 'UP', '225': 'UP', '226': 'UP', '227': 'UP', '228': 'UP', '229': 'UP', '230': 'UP', '231': 'UP', '232': 'UP', '233': 'UP', '234': 'UP', '241': 'UP', '242': 'UP', '243': 'UP', '244': 'UP', '245': 'UP', '246': 'UP', '247': 'UP', '248': 'UK', '249': 'UK', '250': 'UP', '251': 'UP', '252': 'UP', '253': 'UP', '254': 'UP', '255': 'UP', '256': 'UP', '257': 'UP', '258': 'UP', '259': 'UP', '260': 'UP', '261': 'UP', '262': 'UP', '263': 'UP', '264': 'UP', '270': 'UP', '271': 'UP', '272': 'UP', '273': 'UP', '274': 'UP', '275': 'UP', '276': 'UP', '277': 'UP', '278': 'UP', '279': 'UP', '280': 'UP', '281': 'UP', '282': 'UP', '283': 'UP', '284': 'UP', '285': 'UP', '286': 'UP', '287': 'UP', '288': 'UP', '289': 'UP', '300': 'RJ', '301': 'RJ', '302': 'RJ', '303': 'RJ', '304': 'RJ', '305': 'RJ', '306': 'RJ', '307': 'RJ', '308': 'RJ', '309': 'RJ', '310': 'RJ', '311': 'RJ', '312': 'RJ', '313': 'RJ', '314': 'RJ', '315': 'RJ', '316': 'RJ', '317': 'RJ', '318': 'RJ', '319': 'RJ', '320': 'RJ', '321': 'RJ', '322': 'RJ', '323': 'RJ', '324': 'RJ', '325': 'RJ', '326': 'RJ', '327': 'RJ', '328': 'RJ', '329': 'RJ', '330': 'RJ', '331': 'RJ', '332': 'RJ', '333': 'RJ', '334': 'RJ', '335': 'RJ', '336': 'RJ', '337': 'RJ', '338': 'RJ', '339': 'RJ', '340': 'RJ', '341': 'RJ', '342': 'RJ', '343': 'RJ', '344': 'RJ', '345': 'RJ', '360': 'GJ', '361': 'GJ', '362': 'GJ', '363': 'GJ', '364': 'GJ', '365': 'GJ', '366': 'GJ', '367': 'GJ', '368': 'GJ', '369': 'GJ', '370': 'GJ', '371': 'GJ', '372': 'GJ', '373': 'GJ', '374': 'GJ', '375': 'GJ', '376': 'GJ', '377': 'GJ', '378': 'GJ', '379': 'GJ', '380': 'GJ', '381': 'GJ', '382': 'GJ', '383': 'GJ', '384': 'GJ', '385': 'GJ', '386': 'GJ', '387': 'GJ', '388': 'GJ', '389': 'GJ', '390': 'GJ', '391': 'GJ', '392': 'GJ', '393': 'GJ', '394': 'GJ', '395': 'GJ', '396': 'GJ', '397': 'GJ', '398': 'GJ', '399': 'GJ', '400': 'MH', '401': 'MH', '402': 'MH', '403': 'MH', '404': 'MH', '405': 'MH', '406': 'MH', '407': 'MH', '408': 'MH', '409': 'MH', '410': 'MH', '411': 'MH', '412': 'MH', '413': 'MH', '414': 'MH', '415': 'MH', '416': 'MH', '417': 'MH', '418': 'MH', '419': 'MH', '420': 'MH', '421': 'MH', '422': 'MH', '423': 'MH', '424': 'MH', '425': 'MH', '426': 'MH', '427': 'MH', '428': 'MH', '429': 'MH', '430': 'MH', '431': 'MH', '432': 'MH', '433': 'MH', '434': 'MH', '435': 'MH', '440': 'MH', '441': 'MH', '442': 'MH', '443': 'MH', '444': 'MH', '445': 'MH', '446': 'MH', '447': 'MH', '448': 'MH', '449': 'MH', '450': 'MP', '451': 'MP', '452': 'MP', '453': 'MP', '454': 'MP', '455': 'MP', '456': 'MP', '457': 'MP', '458': 'MP', '459': 'MP', '460': 'MP', '461': 'MP', '462': 'MP', '463': 'MP', '464': 'MP', '465': 'MP', '466': 'MP', '467': 'MP', '468': 'MP', '469': 'MP', '470': 'MP', '471': 'MP', '472': 'MP', '473': 'MP', '474': 'MP', '475': 'MP', '476': 'MP', '477': 'MP', '478': 'MP', '479': 'MP', '480': 'MP', '481': 'MP', '482': 'MP', '483': 'MP', '484': 'MP', '485': 'MP', '486': 'MP', '487': 'MP', '488': 'MP', '489': 'MP', '490': 'CG', '491': 'CG', '492': 'CG', '493': 'CG', '494': 'CG', '495': 'CG', '496': 'CG', '497': 'CG', '498': 'CG', '499': 'CG', '500': 'TS', '501': 'TS', '502': 'TS', '503': 'TS', '504': 'TS', '505': 'TS', '506': 'TS', '507': 'TS', '508': 'TS', '509': 'TS', '510': 'TS', '511': 'TS', '512': 'TS', '513': 'TS', '514': 'TS', '515': 'AP', '516': 'AP', '517': 'AP', '518': 'AP', '519': 'AP', '520': 'AP', '521': 'AP', '522': 'AP', '523': 'AP', '524': 'AP', '525': 'AP', '526': 'AP', '527': 'AP', '528': 'AP', '529': 'AP', '530': 'AP', '531': 'AP', '532': 'AP', '533': 'AP', '534': 'AP', '535': 'AP', '560': 'KA', '561': 'KA', '562': 'KA', '563': 'KA', '564': 'KA', '565': 'KA', '566': 'KA', '567': 'KA', '568': 'KA', '569': 'KA', '570': 'KA', '571': 'KA', '572': 'KA', '573': 'KA', '574': 'KA', '575': 'KA', '576': 'KA', '577': 'KA', '578': 'KA', '579': 'KA', '580': 'KA', '581': 'KA', '582': 'KA', '583': 'KA', '584': 'KA', '585': 'KA', '586': 'KA', '587': 'KA', '588': 'KA', '589': 'KA', '590': 'KA', '591': 'KA', '592': 'KA', '593': 'KA', '594': 'KA', '595': 'KA', '596': 'KA', '597': 'KA', '598': 'KA', '599': 'KA', '600': 'TN', '601': 'TN', '602': 'TN', '603': 'TN', '604': 'TN', '605': 'TN', '606': 'TN', '607': 'TN', '608': 'TN', '609': 'TN', '610': 'TN', '611': 'TN', '612': 'TN', '613': 'TN', '614': 'TN', '615': 'TN', '616': 'TN', '617': 'TN', '618': 'TN', '619': 'TN', '620': 'TN', '621': 'TN', '622': 'TN', '623': 'TN', '624': 'TN', '625': 'TN', '626': 'TN', '627': 'TN', '628': 'TN', '629': 'TN', '630': 'TN', '631': 'TN', '632': 'TN', '633': 'TN', '634': 'TN', '635': 'TN', '636': 'TN', '637': 'TN', '638': 'TN', '639': 'TN', '640': 'TN', '641': 'TN', '642': 'TN', '643': 'TN', '644': 'TN', '645': 'TN', '670': 'KL', '671': 'KL', '672': 'KL', '673': 'KL', '674': 'KL', '675': 'KL', '676': 'KL', '677': 'KL', '678': 'KL', '679': 'KL', '680': 'KL', '681': 'KL', '682': 'KL', '683': 'KL', '684': 'KL', '685': 'KL', '686': 'KL', '687': 'KL', '688': 'KL', '689': 'KL', '690': 'KL', '691': 'KL', '692': 'KL', '693': 'KL', '694': 'KL', '695': 'KL', '696': 'KL', '697': 'KL', '698': 'KL', '699': 'KL', '700': 'WB', '701': 'WB', '702': 'WB', '703': 'WB', '704': 'WB', '705': 'WB', '706': 'WB', '707': 'WB', '708': 'WB', '709': 'WB', '710': 'WB', '711': 'WB', '712': 'WB', '713': 'WB', '714': 'WB', '715': 'WB', '716': 'WB', '717': 'WB', '718': 'WB', '719': 'WB', '720': 'WB', '721': 'WB', '722': 'WB', '723': 'WB', '724': 'WB', '725': 'WB', '726': 'WB', '727': 'WB', '728': 'WB', '729': 'WB', '730': 'WB', '731': 'WB', '732': 'WB', '733': 'WB', '734': 'WB', '735': 'WB', '736': 'WB', '737': 'WB', '738': 'WB', '739': 'WB', '740': 'WB', '741': 'WB', '742': 'WB', '743': 'WB', '744': 'AN', '745': 'AN', '746': 'AN', '747': 'AN', '748': 'AN', '749': 'AN', '750': 'AN', '751': 'AN', '752': 'AN', '753': 'AN', '754': 'AN', '755': 'AN', '756': 'AN', '757': 'AN', '758': 'AN', '759': 'AN', '760': 'AN', '761': 'AN', '762': 'AN', '763': 'AN', '764': 'AN', '765': 'AN', '766': 'AN', '767': 'AN', '768': 'AN', '769': 'AN', '770': 'AN', '771': 'AN', '772': 'AN', '773': 'AN', '774': 'AN', '775': 'AN', '776': 'AN', '777': 'AN', '778': 'AN', '779': 'AN', '780': 'AN', '781': 'AS', '782': 'AS', '783': 'AS', '784': 'AS', '785': 'AS', '786': 'AS', '787': 'AS', '788': 'AS', '789': 'AS', '790': 'AR', '791': 'AR', '792': 'AR', '793': 'AR', '794': 'AR', '795': 'AR', '796': 'AR', '797': 'AR', '798': 'AR', '799': 'AR', '800': 'BR', '801': 'BR', '802': 'BR', '803': 'BR', '804': 'BR', '805': 'BR', '806': 'BR', '807': 'BR', '808': 'BR', '809': 'BR', '810': 'BR', '811': 'BR', '812': 'BR', '813': 'BR', '814': 'BR', '815': 'BR', '816': 'BR', '817': 'BR', '818': 'BR', '819': 'BR', '820': 'BR', '821': 'BR', '822': 'BR', '823': 'BR', '824': 'BR', '825': 'BR', '826': 'BR', '827': 'BR', '828': 'BR', '829': 'BR', '830': 'BR', '831': 'BR', '832': 'BR', '833': 'BR', '834': 'BR', '835': 'BR', '836': 'BR', '837': 'BR', '838': 'BR', '839': 'BR', '840': 'BR', '841': 'BR', '842': 'BR', '843': 'BR', '844': 'BR', '845': 'BR', '846': 'BR', '847': 'BR', '848': 'BR', '849': 'BR', '850': 'BR', '851': 'BR', '852': 'BR', '853': 'BR', '854': 'BR', '855': 'BR', '856': 'BR', '857': 'BR', '858': 'BR', '859': 'BR', '860': 'BR', '861': 'BR', '862': 'BR', '863': 'BR', '864': 'BR', '865': 'BR', '866': 'BR', '867': 'BR', '868': 'BR', '869': 'BR', '870': 'BR', '871': 'BR', '872': 'BR', '873': 'BR', '874': 'BR', '875': 'BR', '876': 'BR', '877': 'BR', '878': 'BR', '879': 'BR', '880': 'BR', '881': 'BR', '882': 'BR', '883': 'BR', '884': 'BR', '885': 'BR', '886': 'BR', '887': 'BR', '888': 'BR', '889': 'BR', '890': 'BR', '891': 'BR', '892': 'BR', '893': 'BR', '894': 'BR', '895': 'BR', '896': 'BR', '897': 'BR', '898': 'BR', '899': 'BR'
};

function tlPincodeToProvince(pin) {
  const p3 = pin.slice(0, 3);
  if (TL_PINCODE_PROVINCE_PREFIX3[p3]) return TL_PINCODE_PROVINCE_PREFIX3[p3];
  const d = pin.charAt(0);
  const fallback = { '1': 'DL', '2': 'UP', '3': 'RJ', '4': 'MH', '5': 'TS', '6': 'TN', '7': 'WB', '8': 'BR', '9': 'KL' };
  return fallback[d] || 'MH';
}

const TL_PINCODE_CHECKER_MSG_DEFAULT = {
  errInvalid: '❌ Please enter a valid 6-digit pincode',
  errLeadingZero: '❌ No Indian pincode starts with 0. Please double-check.',
  resultTpl: '✅ Delivery in {days} business days · {zone}{cod}',
  resultTplShiprocket: '✅ Delivery available to {pin} · Est. {days} business days{cod}',
  codYes: ' · COD Available',
  codNo: '',
  days_ne: '5–7',
  days_metro: '1–2',
  days_tier2: '2–3',
  days_tier3: '3–5',
  zone_ne: 'Northeast / Remote',
  zone_metro: 'Metro City',
  zone_tier2: 'Tier 2 City',
  zone_tier3: 'Tier 3 / Town',
  checking: 'Checking delivery…'
};

function getTlPincodeCheckerConfig() {
  const el = document.getElementById('tl-pincode-checker-config-global');
  const defaults = {
    serviceabilityMode: 'shiprocket',
    shiprocketServiceUrl: 'https://toyland-api-service.onrender.com/serviceability',
    pickupPincode: '395007',
    weightKg: 0.5,
    probeVariantId: null,
    errNotServiceable: 'Sorry, we do not deliver to this pincode yet.',
    errNetwork: 'Could not check delivery right now. Please try again.',
    errNotConfigured: 'Delivery check is temporarily unavailable. Please try again later or contact us on WhatsApp.',
    errProxyLocal: 'Delivery check is temporarily unavailable. Please try again later or contact us on WhatsApp.'
  };
  if (!el) return Object.assign({}, defaults);
  try {
    return Object.assign({}, defaults, JSON.parse(el.textContent || '{}'));
  } catch (e) {
    return Object.assign({}, defaults);
  }
}

function getTlPincodeCheckerMessages() {
  const el = document.getElementById('tl-pincode-checker-msgs');
  if (!el) return Object.assign({}, TL_PINCODE_CHECKER_MSG_DEFAULT);
  try {
    const parsed = JSON.parse(el.textContent || '{}');
    return Object.assign({}, TL_PINCODE_CHECKER_MSG_DEFAULT, parsed);
  } catch (e) {
    return Object.assign({}, TL_PINCODE_CHECKER_MSG_DEFAULT);
  }
}

function tlPincodeSleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function tlPincodeShippingParams(pin) {
  const params = new URLSearchParams();
  params.set('shipping_address[zip]', pin);
  params.set('shipping_address[country]', 'India');
  params.set('shipping_address[province]', tlPincodeToProvince(pin));
  return params;
}

async function tlPincodeEnsureProbeCart(probeVariantId) {
  const cart = await tlGetCart();
  const probeId = String(probeVariantId);
  const existing = (cart.items || []).find((i) => String(i.variant_id) === probeId);
  if (existing) return { added: false, lineKey: existing.key };
  if ((cart.item_count || 0) > 0) return { added: false, lineKey: null };
  await tlAddToCart(probeVariantId, 1);
  const cart2 = await tlGetCart();
  const line = (cart2.items || []).find((i) => String(i.variant_id) === probeId);
  return { added: true, lineKey: line ? line.key : null };
}

async function tlPincodeRemoveProbe(lineKey) {
  if (!lineKey) return;
  await fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ updates: { [lineKey]: 0 } })
  });
}

function tlPincodeServiceUrl(cfg) {
  let raw = String((cfg && cfg.shiprocketServiceUrl) || 'https://toyland-api-service.onrender.com/serviceability').trim();
  const devUrl = String((cfg && cfg.shiprocketDevServiceUrl) || 'https://toyland-api-service.onrender.com/serviceability').trim();

  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const isLocal = host === '127.0.0.1' || host === 'localhost';
    const isAppProxyPath = raw.charAt(0) === '/' && raw.indexOf('/apps/') === 0;
    if (isLocal && isAppProxyPath && devUrl) raw = devUrl;
  }

  return raw.replace(/\/$/, '');
}

async function tlPincodeFetchShiprocket(pin, cfg) {
  const base = tlPincodeServiceUrl(cfg);
  if (!base) return { error: 'not_configured' };

  const pickup = (cfg.pickupPincode || '395007').replace(/\D/g, '');
  const weight = cfg.weightKg != null ? cfg.weightKg : 0.5;
  const params = new URLSearchParams({
    delivery_postcode: pin,
    pickup_postcode: pickup,
    weight: String(weight),
    cod: '1'
  });

  const url = `${base}?${params}`;

  const crossOrigin =
    typeof window !== 'undefined' &&
    url.indexOf('http') === 0 &&
    !url.startsWith(window.location.origin);

  let res;
  try {
    res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: crossOrigin ? 'omit' : 'same-origin'
    });
  } catch (e) {
    return { error: 'network', hint: crossOrigin ? 'proxy_not_running' : 'fetch_failed' };
  }

  const contentType = (res.headers.get('content-type') || '').toLowerCase();
  const isJson = contentType.indexOf('json') > -1;
  let text = '';
  let data = null;

  try {
    text = await res.text();
    if (text && (isJson || text.charAt(0) === '{')) data = JSON.parse(text);
  } catch (e) {
    if (text.trim().charAt(0) === '<' || res.status === 404) {
      return { error: 'not_configured', hint: 'app_proxy_or_html' };
    }
    return { error: 'network', hint: 'invalid_json' };
  }

  if (res.status === 404 || res.status === 502 || res.status === 503) {
    return { error: 'not_configured', detail: data, hint: 'http_' + res.status };
  }
  if (!res.ok) {
    return { error: 'upstream', detail: data };
  }
  if (!data || typeof data !== 'object') {
    return { error: 'not_configured', hint: 'empty_response' };
  }

  return { data };
}

function tlPincodeRenderShiprocketSuccess(pin, result, msg, sr) {
  const days = sr.eddDays ? String(sr.eddDays) : '';
  const codPart = sr.codAvailable ? (msg.codYes != null ? msg.codYes : TL_PINCODE_CHECKER_MSG_DEFAULT.codYes) : '';
  const tpl = msg.resultTplShiprocket || TL_PINCODE_CHECKER_MSG_DEFAULT.resultTplShiprocket;
  const body = String(tpl)
    .replace(/\{pin\}/g, pin)
    .replace(/\{days\}/g, days)
    .replace(/\{cod\}/g, codPart);

  result.className = 'tl-pincode__result tl-pincode__result--ok';
  result.innerHTML = body;
}

async function tlPincodeFetchShopifyShippingRates(pin) {
  const params = tlPincodeShippingParams(pin);
  const qs = params.toString();

  await fetch('/cart/prepare_shipping_rates.json', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
    body: qs
  });

  for (let i = 0; i < 8; i++) {
    await tlPincodeSleep(i === 0 ? 200 : 450);
    const res = await fetch('/cart/async_shipping_rates.json?' + qs, {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) continue;
    let data;
    try {
      data = await res.json();
    } catch (e) {
      continue;
    }
    if (data && Array.isArray(data.shipping_rates) && data.shipping_rates.length > 0) {
      return data.shipping_rates;
    }
    if (data && data.shipping_rates === null) continue;
    if (data && Array.isArray(data.shipping_rates) && data.shipping_rates.length === 0) {
      return [];
    }
  }

  const direct = await fetch('/cart/shipping_rates.json?' + qs, { headers: { Accept: 'application/json' } });
  if (direct.ok) {
    const data = await direct.json();
    if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
  }
  return null;
}

function tlPincodeRenderZoneResult(pin, result, msg) {
  const data = tlPincodeZone(pin);
  const zk = data.zoneKey || 'tier3';
  const zoneKeyName = 'zone_' + zk;
  const daysKeyName = 'days_' + zk;
  const zoneLabel = (msg[zoneKeyName] != null ? msg[zoneKeyName] : TL_PINCODE_CHECKER_MSG_DEFAULT[zoneKeyName]) || '';
  const dayStr =
    (msg[daysKeyName] != null ? msg[daysKeyName] : TL_PINCODE_CHECKER_MSG_DEFAULT[daysKeyName]) || '';
  const codYesPart = msg.codYes != null ? msg.codYes : TL_PINCODE_CHECKER_MSG_DEFAULT.codYes;
  const codNoRaw = msg.codNo != null ? msg.codNo : TL_PINCODE_CHECKER_MSG_DEFAULT.codNo;
  let codPart = '';
  if (data.cod) codPart = codYesPart;
  else {
    const s = String(codNoRaw ?? '').trim();
    const low = s.toLowerCase();
    codPart = s === '' || low === '(none)' || low === '(blank)' ? '' : s;
  }

  const tpl = msg.resultTpl || TL_PINCODE_CHECKER_MSG_DEFAULT.resultTpl;
  const body = String(tpl)
    .replace(/\{days\}/g, dayStr)
    .replace(/\{zone\}/g, zoneLabel)
    .replace(/\{cod\}/g, codPart == null ? '' : String(codPart))
    .replace(/\{pin\}/g, pin);

  result.className = 'tl-pincode__result tl-pincode__result--ok';
  result.innerHTML = body;
}

function tlPincodeShowError(result, text) {
  result.className = 'tl-pincode__result tl-pincode__result--err';
  result.textContent = text;
}

function tlPincodeSetLoading(btn, loading, msg) {
  if (!btn) return;
  btn.disabled = loading;
  if (loading) {
    btn.dataset.tlPincodePrevLabel = btn.textContent;
    btn.textContent = msg.checking || TL_PINCODE_CHECKER_MSG_DEFAULT.checking;
  } else if (btn.dataset.tlPincodePrevLabel) {
    btn.textContent = btn.dataset.tlPincodePrevLabel;
    delete btn.dataset.tlPincodePrevLabel;
  }
}

async function tlRunPincodeCheck(pin, result, msg, cfg) {
  const mode = (cfg && cfg.serviceabilityMode) || 'shiprocket';

  if (mode === 'zones') {
    tlPincodeRenderZoneResult(pin, result, msg);
    return;
  }

  if (mode === 'shiprocket') {
    const srRes = await tlPincodeFetchShiprocket(pin, cfg);
    if (srRes.error === 'not_configured') {
      const isLocal =
        typeof window !== 'undefined' &&
        window.location &&
        (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');
      const localMsg = cfg.errProxyLocal || TL_PINCODE_CHECKER_MSG_DEFAULT.errProxyLocal;
      tlPincodeShowError(
        result,
        isLocal && (srRes.hint === 'app_proxy_or_html' || srRes.hint === 'proxy_not_running')
          ? localMsg
          : cfg.errNotConfigured || TL_PINCODE_CHECKER_MSG_DEFAULT.errNotConfigured
      );
      return;
    }
    if (srRes.error) {
      const isLocal =
        typeof window !== 'undefined' &&
        window.location &&
        (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');
      if (isLocal && srRes.hint === 'proxy_not_running') {
        tlPincodeShowError(result, cfg.errProxyLocal || TL_PINCODE_CHECKER_MSG_DEFAULT.errProxyLocal);
      } else {
        tlPincodeShowError(result, cfg.errNetwork || TL_PINCODE_CHECKER_MSG_DEFAULT.errNetwork);
      }
      return;
    }
    const sr = srRes.data || {};
    if (sr.serviceable !== true) {
      tlPincodeShowError(result, cfg.errNotServiceable || TL_PINCODE_CHECKER_MSG_DEFAULT.errNotServiceable);
      return;
    }
    tlPincodeRenderShiprocketSuccess(pin, result, msg, sr);
    return;
  }

  if (mode === 'shopify_shipping') {
    if (!cfg.probeVariantId) {
      const cart = await tlGetCart();
      if ((cart.item_count || 0) === 0) {
        tlPincodeShowError(result, cfg.errNotConfigured || TL_PINCODE_CHECKER_MSG_DEFAULT.errNotConfigured);
        return;
      }
    }

    let probe = { added: false, lineKey: null };
    try {
      if (cfg.probeVariantId) {
        probe = await tlPincodeEnsureProbeCart(cfg.probeVariantId);
      }
      const rates = await tlPincodeFetchShopifyShippingRates(pin);
      if (rates === null) {
        tlPincodeShowError(result, cfg.errNetwork || TL_PINCODE_CHECKER_MSG_DEFAULT.errNetwork);
        return;
      }
      if (!rates.length) {
        tlPincodeShowError(result, cfg.errNotServiceable || TL_PINCODE_CHECKER_MSG_DEFAULT.errNotServiceable);
        return;
      }

      tlPincodeRenderShiprocketSuccess(pin, result, msg, { eddDays: '', codAvailable: true });
    } catch (e) {
      tlPincodeShowError(result, cfg.errNetwork || TL_PINCODE_CHECKER_MSG_DEFAULT.errNetwork);
    } finally {
      if (probe.added && probe.lineKey) {
        try {
          await tlPincodeRemoveProbe(probe.lineKey);
          const cart = await tlGetCart();
          document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: cart }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart, bubbles: true }));
        } catch (e) { /* ignore */ }
      }
    }
    return;
  }

  tlPincodeShowError(result, cfg.errNotServiceable || TL_PINCODE_CHECKER_MSG_DEFAULT.errNotServiceable);
}

async function tlCheckPincodeFromElements(input, result, btn) {
  if (!input || !result) return;
  const pin = (input.value || '').replace(/\D/g, '').trim();
  const msg = getTlPincodeCheckerMessages();
  const cfg = getTlPincodeCheckerConfig();

  if (pin.length !== 6) {
    tlPincodeShowError(result, msg.errInvalid || TL_PINCODE_CHECKER_MSG_DEFAULT.errInvalid);
    return;
  }

  if (pin.charAt(0) === '0') {
    tlPincodeShowError(result, msg.errLeadingZero || msg.errInvalid || TL_PINCODE_CHECKER_MSG_DEFAULT.errInvalid);
    return;
  }

  tlPincodeSetLoading(btn, true, msg);
  result.className = 'tl-pincode__result';
  result.textContent = msg.checking || TL_PINCODE_CHECKER_MSG_DEFAULT.checking;

  await tlRunPincodeCheck(pin, result, msg, cfg);
  tlPincodeSetLoading(btn, false, msg);
}

function tlCheckPincode() {
  const input = document.getElementById('tl-pincode-input');
  const result = document.getElementById('tl-pincode-result');
  const btn = document.querySelector('[data-tl-pincode-btn]');
  tlCheckPincodeFromElements(input, result, btn);
}

// Event delegation for [data-tl-pincode-btn] and PDP form
document.addEventListener('click', (e) => {
  if (e.target.closest('[data-tl-pincode-btn]')) {
    e.preventDefault();
    tlCheckPincode();
    return;
  }

  const chip = e.target.closest('[data-tl-pincode-city]');
  if (chip) {
    const pin = chip.dataset.tlPincodeCity;
    const input = document.getElementById('tl-pincode-input');
    if (input) {
      input.value = pin;
      tlCheckPincode();
      input.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }
});

document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-toyland-pincode]');
  if (!form) return;
  e.preventDefault();
  const input = form.querySelector('input');
  const result = form.querySelector('[data-toyland-pincode-result]');
  const btn = form.querySelector('button[type="submit"]');
  if (result) result.removeAttribute('hidden');
  tlCheckPincodeFromElements(input, result, btn);
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Enter') return;
  if (e.target.id === 'tl-pincode-input') {
    e.preventDefault();
    tlCheckPincode();
  }
});

document.addEventListener('input', (e) => {
  const input = e.target;
  if (input.id !== 'tl-pincode-input' && !input.closest('[data-toyland-pincode]')) return;
  const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
  if (input.value !== cleaned) input.value = cleaned;
});


// =============================================================================
// PRODUCT TABS (section: toyland-product-tabs)
// =============================================================================

function initProductTabs() {
  const tabs = document.querySelectorAll('.tl-tabs__btn');
  const panels = document.querySelectorAll('.tl-tabs__panel');
  if (!tabs.length) return;

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;
      tabs.forEach(t => { t.classList.remove('tl-tabs__btn--active'); t.setAttribute('aria-selected', 'false'); });
      panels.forEach(p => { p.hidden = true; });
      tab.classList.add('tl-tabs__btn--active');
      tab.setAttribute('aria-selected', 'true');
      const activePanel = document.querySelector('[data-panel="' + target + '"]');
      if (activePanel) activePanel.hidden = false;
    });
  });
}

document.addEventListener('DOMContentLoaded', initProductTabs);


// =============================================================================
// PDP SOCIAL PROOF (snippet: toyland-pdp-social-proof)
// Simulated buyer count + rotating cities. Replace with real data later.
// =============================================================================

function initSocialProof() {
  const buyerEl = document.getElementById('tl-buyer-count');
  const citiesEl = document.getElementById('tl-buyer-cities');
  if (!buyerEl) return;

  // Simulated buyer count — random between 12-48, seeded by product ID from URL
  var seed = 0;
  var path = window.location.pathname;
  for (var i = 0; i < path.length; i++) seed += path.charCodeAt(i);
  var count = 12 + (seed % 37);
  buyerEl.textContent = count;

  // Rotate cities every 5 seconds
  var cityGroups = [
    'Mumbai, Delhi, Bangalore',
    'Pune, Hyderabad, Chennai',
    'Ahmedabad, Jaipur, Kolkata',
    'Surat, Lucknow, Indore',
    'Chandigarh, Kochi, Nagpur'
  ];
  if (citiesEl) {
    var idx = 0;
    setInterval(function() {
      idx = (idx + 1) % cityGroups.length;
      citiesEl.style.opacity = '0';
      setTimeout(function() {
        citiesEl.textContent = cityGroups[idx];
        citiesEl.style.opacity = '1';
      }, 200);
    }, 5000);
    citiesEl.style.transition = 'opacity 0.2s';
  }
}

document.addEventListener('DOMContentLoaded', initSocialProof);


// =============================================================================
// STICKY ATC BAR ENHANCED (snippet: toyland-sticky-atc)
// =============================================================================

function initStickyATC() {
  var mainATC = document.querySelector('.product-form__submit');
  var stickyBar = document.getElementById('tl-sticky-atc');
  if (!mainATC || !stickyBar) return;

  // Hide entirely on desktop — CSS handles it but guard here too
  if (window.matchMedia('(min-width: 768px)').matches) return;

  // Show/hide via slide-up transform (IntersectionObserver on main ATC button)
  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      var visible = !entry.isIntersecting;
      stickyBar.classList.toggle('tl-sticky-atc--visible', visible);
      stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }, { threshold: 0.1 });
  observer.observe(mainATC);

  // ATC button — AJAX add to cart, opens Dawn cart drawer on success
  var atcBtn = document.getElementById('tl-sticky-atc-btn');
  if (atcBtn) {
    atcBtn.addEventListener('click', function() {
      var variantId = atcBtn.dataset.variantId;
      if (!variantId) return;

      var cartDrawer = document.querySelector('cart-drawer');

      // Use FormData + sections param so Dawn's renderContents can update & open the drawer
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        formData.append('sections', cartDrawer.getSectionsToRender().map(function(s) { return s.id; }));
        formData.append('sections_url', window.location.pathname);
        cartDrawer.setActiveElement(atcBtn);
      }

      atcBtn.disabled = true;
      atcBtn.textContent = 'Adding…';

      var url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      fetch(url, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.status) {
          // Shopify returned an error (e.g. out of stock, per-order cap).
          // Say so — a button that just snaps back to "Add to Cart" reads as broken.
          if (!data.tl_limit) tlToast(data.description || data.message || 'Unable to add to cart', 'error');
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to Cart';
          return;
        }
        // Update drawer HTML and open it (renderContents calls this.open() internally)
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(data);
        }
        document.dispatchEvent(new CustomEvent('tl:cart:updated', { detail: data }));
        atcBtn.textContent = '✓ Added!';
        setTimeout(function() {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Add to Cart';
        }, 1500);
      })
      .catch(function(err) {
        tlToast(tlCartErrorMessage(err), 'error');
        atcBtn.disabled = false;
        atcBtn.textContent = 'Add to Cart';
      });
    });
  }
}

document.addEventListener('DOMContentLoaded', initStickyATC);

// FBT (Frequently bought together) — implemented in toyland-redesign.js (formatMoney + cart refresh)

// =============================================================================
// CART PAGE: COD BANNER (snippet: toyland-cart-cod-banner)
// [data-tl-cod-copy] — copies the value attr to clipboard
// [data-tl-cod-dismiss] — hides all .tl-cod-banner in the page (no persistence;
//   banner is back on full reload or when cart section HTML is replaced)
// =============================================================================

document.addEventListener('click', (e) => {
  // Copy code button — shared by the cart COD banner ("Copy Code") and the PDP
  // "Offers for you" card ("Copy"), so the original label is remembered and put
  // back rather than hardcoded; a second button would otherwise be relabelled.
  const copyBtn = e.target.closest('[data-tl-cod-copy]');
  if (copyBtn) {
    const code = copyBtn.dataset.tlCodCopy;
    // Write to the inner label span when there is one. The PDP offers button holds
    // two icons alongside its text, and rewriting the BUTTON's textContent would
    // delete them from the DOM on the first click — permanently, since the restore
    // puts back only the label. Buttons without a span (cart COD banner) are
    // relabelled directly, exactly as before.
    const labelEl = copyBtn.querySelector('[data-tl-copy-text]') || copyBtn;
    if (copyBtn.dataset.tlCopyLabel === undefined) {
      copyBtn.dataset.tlCopyLabel = labelEl.textContent.trim();
    }
    const restore = () => {
      labelEl.textContent = copyBtn.dataset.tlCopyLabel;
      copyBtn.classList.remove('is-copied');
    };
    const flash = (text) => {
      labelEl.textContent = text;
      // Re-clicking mid-flash must not let an earlier timer restore the label early.
      clearTimeout(copyBtn._tlCopyTimer);
      copyBtn._tlCopyTimer = setTimeout(restore, 2500);
    };
    // clipboard is unavailable over plain http and in some in-app browsers — fall
    // back to selecting the code so a long-press → Copy still works.
    const clip = navigator.clipboard && navigator.clipboard.writeText(code);
    if (clip) {
      clip.then(() => {
        // Presentation is CSS's job via .is-copied — an inline background used to be
        // set here, which the offers button's own success style could not override.
        copyBtn.classList.add('is-copied');
        flash(copyBtn.dataset.tlCopiedLabel || 'Copied! ✓');
      }).catch(() => flash('Select & copy'));
    } else {
      flash('Select & copy');
    }
    return;
  }

  const dismissEl = e.target.closest('[data-tl-cod-dismiss]');
  if (dismissEl) {
    document.querySelectorAll('.tl-cod-banner').forEach((el) => {
      el.style.display = 'none';
    });
  }
});


// =============================================================================
// ANNOUNCEMENT BAR DISMISS (section: toyland-announcement)
// [data-tl-announce-dismiss] — hides bar + sets sessionStorage flag
// =============================================================================

document.addEventListener('click', (e) => {
  if (e.target.closest('[data-tl-announce-dismiss]')) {
    const bar = document.getElementById('tl-announce');
    if (bar) {
      // Hide the full Shopify section wrapper so no gap is left behind
      const section = bar.closest('.shopify-section') || bar;
      section.style.display = 'none';
      sessionStorage.setItem('tl-announce-dismissed', '1');
    }
  }
});

// Auto-hide announcement bar if already dismissed this session
document.addEventListener('DOMContentLoaded', () => {
  if (sessionStorage.getItem('tl-announce-dismissed')) {
    const bar = document.getElementById('tl-announce');
    if (bar) {
      const section = bar.closest('.shopify-section') || bar;
      section.style.display = 'none';
    }
  }
});


// =============================================================================
// HEADER — Frosted glass scroll behavior
// Adds .tl-scrolled when page scrolled > 10px (triggers border + bg opacity).
// Header always stays visible — no hide-on-scroll-down.
// Uses requestAnimationFrame — no debounce needed.
// =============================================================================

(function () {
  let ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateHeader);
      ticking = true;
    }
  }

  function updateHeader() {
    const wrapper = document.querySelector('.header-wrapper');
    if (!wrapper) { ticking = false; return; }

    // Scrolled class — triggers border + stronger bg
    if (window.scrollY > 10) {
      wrapper.classList.add('tl-scrolled');
    } else {
      wrapper.classList.remove('tl-scrolled');
    }

    ticking = false;
  }

  window.addEventListener('scroll', onScroll, { passive: true });
})();


// =============================================================================
// FILTER BAR — Sticky shadow on scroll
// Adds .is-sticky to #tl-fbar when page scrolls past it.
// =============================================================================

(function initFilterBar() {
  const fbar = document.getElementById('tl-fbar');
  if (!fbar) return;

  const sentinel = document.createElement('div');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;height:1px;pointer-events:none;';
  fbar.parentElement.insertBefore(sentinel, fbar);

  const obs = new IntersectionObserver(
    ([entry]) => {
      fbar.classList.toggle('is-sticky', !entry.isIntersecting);
    },
    { rootMargin: '0px', threshold: 0 }
  );
  obs.observe(sentinel);
})();


// =============================================================================
// QUICK-ADD — see initTlQuickAdd() above.
//
// A second [data-tl-quick-add] click listener used to live here, duplicating the
// one above. Because it registered first it was the copy that actually ran, and it
// suppressed the other by disabling the button — see the note up there for why
// relying on that was unsafe. Its loading / "Added!" / error states were folded
// into the surviving handler, so nothing is lost by deleting it.
// =============================================================================

/* =============================================================================
   Footer accordions — closed on mobile, always open on desktop
   ============================================================================= */
(function () {
  var BREAKPOINT = 768;

  function setFooterAccordions() {
    var accordions = document.querySelectorAll('.tl-footer__accordion');
    var isMobile = window.innerWidth < BREAKPOINT;
    accordions.forEach(function (el) {
      if (isMobile) {
        el.removeAttribute('open');
      } else {
        el.setAttribute('open', '');
      }
    });
  }

  document.addEventListener('DOMContentLoaded', setFooterAccordions);

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(setFooterAccordions, 100);
  }, { passive: true });
}());



// =============================================================================
// SCROLL REVEAL — Generic IntersectionObserver for data-tl-reveal / data-tl-stagger
// =============================================================================

function initScrollReveal() {
  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('tl-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

  document.querySelectorAll('[data-tl-reveal], [data-tl-stagger]').forEach(function (el) {
    observer.observe(el);
  });
}

document.addEventListener('DOMContentLoaded', initScrollReveal);


// =============================================================================
// SKELETON LOADING — Show shimmer cards while facet-filtered products reload
// =============================================================================

(function () {
  // Build skeleton HTML for N cards
  function tlCreateSkeletonGrid(count) {
    var n = count || 4;
    var html = '<div class="tl-skel-grid">';
    for (var i = 0; i < n; i++) {
      html +=
        '<div class="tl-skel-card">' +
          '<div class="tl-skeleton tl-skel-card__img"></div>' +
          '<div class="tl-skeleton tl-skel-card__title"></div>' +
          '<div class="tl-skeleton tl-skel-card__price"></div>' +
        '</div>';
    }
    html += '</div>';
    return html;
  }

  function initSkeletonObserver() {
    var container = document.getElementById('ProductGridContainer');
    if (!container) return;

    // Watch .collection element for the `loading` class Dawn adds during fetch
    var itemObserver = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;
        var collection = mutation.target;
        if (collection.classList.contains('loading')) {
          // Match skeleton count to current visible cards (2–8)
          var existing = collection.querySelectorAll('.card-wrapper, [data-product-card], li.grid__item');
          var count = Math.min(Math.max(existing.length || 4, 2), 8);
          collection.innerHTML = tlCreateSkeletonGrid(count);
        }
        // On loading complete, Dawn replaces #ProductGridContainer innerHTML — skeleton gone automatically
      });
    });

    function attachToCollection() {
      var collectionEl = container.querySelector('.collection');
      if (collectionEl) {
        itemObserver.observe(collectionEl, { attributes: true });
      }
    }

    // Re-attach after Dawn swaps out #ProductGridContainer's children
    var containerObserver = new MutationObserver(function () {
      itemObserver.disconnect();
      attachToCollection();
    });
    containerObserver.observe(container, { childList: true });

    attachToCollection();
  }

  document.addEventListener('DOMContentLoaded', initSkeletonObserver);
}());


// =============================================================================
// TRUST BAR — Scroll-reveal: fade items in from bottom with staggered delay
// =============================================================================

(function () {
  var items = document.querySelectorAll('.tl-tbar__item');
  if (!items.length) return;

  var observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('tl-tbar--visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.2 });

  items.forEach(function (item) {
    observer.observe(item);
  });
}());


// =============================================================================
// LIST COLLECTIONS — client sort (current page only; server default on load)
// =============================================================================
(function () {
  var LCO_OPTION_CHECK =
    '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

  function getCells(list) {
    return Array.prototype.slice.call(list.querySelectorAll('.tl-lcoll-grid__cell'));
  }

  function initListCollectionsSort() {
    var root = document.querySelector('[data-tl-lcoll]');
    if (!root) return;
    var list = root.querySelector('[data-tl-lcoll-list]');
    if (!list) return;

    var initial = getCells(list);

    function getKey(el) {
      return (el.getAttribute('data-tl-lcoll-key') || '').toLowerCase();
    }
    function getCount(el) {
      return parseInt(el.getAttribute('data-tl-lcoll-count'), 10) || 0;
    }
    function getDate(el) {
      var d = parseInt(el.getAttribute('data-tl-lcoll-date'), 10);
      return isNaN(d) ? 0 : d;
    }

    function applySort(mode) {
      if (mode === 'featured') {
        initial.forEach(function (n) {
          list.appendChild(n);
        });
        return;
      }
      var arr = getCells(list).slice();
      if (mode === 'alpha') {
        arr.sort(function (a, b) {
          return getKey(a).localeCompare(getKey(b));
        });
      } else if (mode === 'alpha_desc') {
        arr.sort(function (a, b) {
          return getKey(b).localeCompare(getKey(a));
        });
      } else if (mode === 'products_desc') {
        arr.sort(function (a, b) {
          return getCount(b) - getCount(a);
        });
      } else if (mode === 'products_asc') {
        arr.sort(function (a, b) {
          return getCount(a) - getCount(b);
        });
      } else if (mode === 'date_desc') {
        arr.sort(function (a, b) {
          return getDate(b) - getDate(a);
        });
      } else if (mode === 'date_asc') {
        arr.sort(function (a, b) {
          return getDate(a) - getDate(b);
        });
      }
      arr.forEach(function (n) {
        list.appendChild(n);
      });
    }

    function setSortUI(sortRoot, mode) {
      var valueEl = sortRoot.querySelector('[data-tl-lcoll-sort-value]');
      var trigger = sortRoot.querySelector('[data-tl-lcoll-sort-trigger]');
      var menu = sortRoot.querySelector('[data-tl-lcoll-sort-menu]');
      if (!valueEl) return;
      var opts = sortRoot.querySelectorAll('[data-tl-lcoll-value]');
      var label = null;
      opts.forEach(function (li) {
        var v = li.getAttribute('data-tl-lcoll-value');
        var on = v === mode;
        li.classList.toggle('is-active', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
        li.querySelectorAll('svg').forEach(function (s) {
          s.remove();
        });
        if (on) {
          li.insertAdjacentHTML('beforeend', LCO_OPTION_CHECK);
          label = li.getAttribute('data-tl-lcoll-label') || li.textContent.trim();
        }
      });
      if (label) valueEl.textContent = label;
      if (menu) menu.setAttribute('hidden', '');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    var sortRoots = Array.prototype.slice.call(root.querySelectorAll('[data-tl-lcoll-sort-root]'));

    function closeAllLcollMenus() {
      sortRoots.forEach(function (sortRoot) {
        var menuEl = sortRoot.querySelector('[data-tl-lcoll-sort-menu]');
        var trig = sortRoot.querySelector('[data-tl-lcoll-sort-trigger]');
        if (menuEl) menuEl.setAttribute('hidden', '');
        if (trig) trig.setAttribute('aria-expanded', 'false');
      });
    }

    if (sortRoots.length) {
      sortRoots.forEach(function (sortRoot) {
        var menu = sortRoot.querySelector('[data-tl-lcoll-sort-menu]');
        var trigger = sortRoot.querySelector('[data-tl-lcoll-sort-trigger]');
        if (!menu || !trigger) return;

        trigger.addEventListener('click', function (e) {
          e.stopPropagation();
          var open = !menu.hasAttribute('hidden');
          if (open) {
            closeAllLcollMenus();
          } else {
            closeAllLcollMenus();
            menu.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });

        menu.addEventListener('click', function (e) {
          var opt = e.target.closest('[data-tl-lcoll-value]');
          if (!opt || !menu.contains(opt)) return;
          e.preventDefault();
          var v = opt.getAttribute('data-tl-lcoll-value');
          if (!v) return;
          applySort(v);
          sortRoots.forEach(function (r) {
            setSortUI(r, v);
          });
        });
      });

      document.addEventListener('click', function (e) {
        var inside = sortRoots.some(function (sr) {
          return sr.contains(e.target);
        });
        if (!inside) closeAllLcollMenus();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeAllLcollMenus();
      });
    } else {
      var select = root.querySelector('[data-tl-lcoll-sort-select]');
      if (select) {
        select.addEventListener('change', function () {
          applySort(select.value);
        });
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initListCollectionsSort);
  } else {
    initListCollectionsSort();
  }
}());
// =============================================================================
// AUTO-APPLY DISCOUNT CODE
// -----------------------------------------------------------------------------
// Applies the code from Theme settings -> Rynoka - Discounts on the shopper's behalf,
// so an advertised code ("Flat Rs.50 off your first order - code WELCOME50") is on the
// cart without them typing it - and, unlike a discount link, VISIBLE in the drawer.
//
// Uses POST /cart/update.js with a `discount` parameter. Shopify added discounts to
// that endpoint in May 2025:
//   https://shopify.dev/changelog/cart-ajax-api-discounts-support-on-cartupdatejs
// This supersedes the older /discount/<code> link, which only sets a cookie consumed at
// checkout - the theme's cart object never saw it, so the saving was invisible until
// the payment page. Applying through the cart API puts the code in
// cart_level_discount_applications, which is what the drawer summary renders.
// /discount/<code> is kept ONLY as the fallback below.
//
// Shopify still enforces every rule set on the code in Admin: minimum spend, expiry,
// and one use per customer. That per-customer cap is why this is worth doing at all -
// an automatic discount would be visible too, but automatic discounts have no usage
// limits, so a "first order" offer would apply to every repeat order forever.
//
// ANTI-STACKING: this never fires when the cart already carries ANY cart-level
// discount. That covers both an automatic discount running at the same time (codes
// usually have combinesWith.orderDiscounts = true, so both would apply and the shopper
// would get the offer twice) and a campaign code the shopper arrived with.
//
// The code and the on/off switch come from snippets/toyland-auto-discount.liquid.
// Never hardcode a code here - the merchant changes it in Theme settings.
// =============================================================================
(function () {
  var cfg = document.querySelector('[data-tl-auto-discount]');
  if (!cfg) return; // switched off, or no code set

  var code = (cfg.getAttribute('data-tl-discount-code') || '').trim();
  if (!code) return;

  var inFlight = false;
  /* The cart total the last attempt was made at. Re-attempting only when the total
     MOVES is what stops a cart sitting below the code's minimum spend from firing a
     request on every quantity change: below the minimum Shopify accepts the call but
     applies nothing, so there is no applied-code flag to test against. When the shopper
     adds enough to cross the minimum the total changes, and it is tried again. */
  var lastTriedTotal = -1;
  var LINK_FALLBACK_KEY = 'tl-auto-discount-link';

  function cartDiscounts(cart) {
    var apps = cart && cart.cart_level_discount_applications;
    return Array.isArray(apps) ? apps : [];
  }

  function hasOurCode(cart) {
    var wanted = code.toUpperCase();
    return cartDiscounts(cart).some(function (d) {
      return String((d && d.title) || '').toUpperCase() === wanted;
    });
  }

  /* Last resort, once per session: the cookie route. It cannot show in the cart, but it
     does carry the code into checkout, so the shopper still gets the money off if the
     cart API declined to apply it. */
  function applyViaLink() {
    try {
      if (sessionStorage.getItem(LINK_FALLBACK_KEY) === code) return;
      sessionStorage.setItem(LINK_FALLBACK_KEY, code);
    } catch (e) {
      /* No storage (privacy mode): skip the fallback rather than re-requesting it on
         every cart change. The cart API above is the path that matters. */
      return;
    }
    fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' }).catch(
      function () {}
    );
  }

  function maybeApply(cart) {
    if (inFlight) return;
    if (!cart || !cart.item_count) return; // nothing to discount yet

    /* Any cart-level discount already present wins - see ANTI-STACKING above. This is
       also the re-entry guard: applying publishes cartUpdate, which lands back here
       with the code now in the array. */
    if (cartDiscounts(cart).length > 0) return;
    if (cart.total_price === lastTriedTotal) return;
    lastTriedTotal = cart.total_price;

    inFlight = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code })
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (updated) {
        if (!updated) return;
        if (hasOurCode(updated)) {
          /* Repaint the drawer summary so the discount row appears immediately. */
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'tl-auto-discount',
              cartData: updated
            });
          }
        } else {
          /* Accepted the request but applied nothing - shopper is below the minimum,
             has already used their one, or the code is expired. Nothing to show them:
             this is a convenience they never asked for, and an error about a discount
             they did not type would be noise. */
          applyViaLink();
        }
      })
      .catch(function () {
        applyViaLink();
      })
      .then(function () {
        inFlight = false;
      });
  }

  /* Every cart write - including Add to Cart, which is the moment the cart first has
     something to discount. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'tl-auto-discount') return;
      if (event && event.cartData) maybeApply(event.cartData);
      else fetchAndApply();
    });
  }

  function fetchAndApply() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(maybeApply)
      .catch(function () {});
  }

  /* Landing with items already in the cart (a returning session, or a cart restored
     from a previous visit) - the cartUpdate subscription alone would never fire.
     Off the critical path so it never competes with product images during load. */
  function initialCheck() {
    if (cfg.getAttribute('data-tl-cart-has-items') !== 'true') return;
    fetchAndApply();
  }

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(initialCheck, { timeout: 3000 });
  } else {
    setTimeout(initialCheck, 1200);
  }
}());
